import React, {Component} from 'react';

import {Parser} from 'html-to-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AccordionUi from 'Frontend/components/AccordionUi';
import PageLayout from 'Frontend/components/PageLayout';
import {PageConfig} from 'Server/models';

import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import dbConnect from 'Server/utils/dbConnect';
import {populateFooterItems, populateMenuItems} from 'Server/utils/pagePopulations';

/**
 * FAQ Page class.
 *
 * @export
 * @class FAQ
 * @extends {Component}
 */
export default class FAQ extends Component {
    static propTypes = {
        page: PropTypes.shape({
            faq: PropTypes.array,
            headline: PropTypes.string,
            // eslint-disable-next-line @nfq/sort-keys
            seoDescription: PropTypes.string,
            seoKeywordList: PropTypes.array,
            // eslint-disable-next-line react/boolean-prop-naming
            seoNoFollow: PropTypes.bool,
            // eslint-disable-next-line react/boolean-prop-naming
            seoNoIndex: PropTypes.bool,
            seoPageTitle: PropTypes.string
        })
    }

    static defaultProps = {
        page: {
            faq: [],
            seoDescription: '',
            seoKeywordList: [],
            seoNoFollow: false,
            seoNoIndex: false,
            seoPageTitle: ''
        }
    }

    htmlParser = new Parser();

    /**
     * Gets the layout for this page.
     *
     * @static
     * @param {Object} router        The router object from nextjs.
     * @param {Object} pageProps     All page props given from server.
     * @param {Object} PageComponent The page component to render.
     *
     * @returns {JSX} The complete page.
     * @memberof FAQ
     */
    static getLayout(router, pageProps, PageComponent) {
        // eslint-disable-next-line no-param-reassign
        pageProps.footer.partner = null;

        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <PageLayout router={router} {...pageProps}>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <PageComponent {...pageProps} />
            </PageLayout>
        );
    }

    /**
     * Renders the Component.
     *
     * @returns {Component} React Component.
     * @memberof FAQ
     */
    render() {
        const {page: {faq, headline}} = this.props;

        return (
            <ContentWrapper>
                <Headline>{headline}</Headline>
                {faq.map(group => (
                    <Group key={group._id}>
                        <GroupHead>
                            {group.headline}
                        </GroupHead>
                        {group.questions.map(question => (
                            <AccordionUi
                                key={question._id}
                                accordionBody={question.text}
                                accordionName={question.headline}
                            />
                        ))}
                    </Group>
                ))}
                {/* <Content>
                    {this.htmlParser.parseWithInstructions(
                        `<div data-module="content">${content}</div>`,
                        isValidNode,
                        processingInstructions
                    )}
                </Content> */}
            </ContentWrapper>
        );
    }
}

const ContentWrapper = styled.div`
    align-items: flex-start;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: flex-start;
    margin: calc(${clampCss(56, 'm')} + 35px) auto ${clampCss(84, 'm')} auto;
    max-width: 100%;
    position: relative;
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.DESKTOP.UP} {
        margin-bottom: max(242px, ${pxToVw(242, 'd')});
        margin-top: calc(max(124px, ${pxToVw(124, 'd')}) + 56px);
        max-width: 100%;
        padding: 0 max(220px, ${pxToVw(220, 'd')}) 0 max(220px, ${pxToVw(220, 'd')});
        width: max(1320px, ${pxToVw(1320, 'd')});
    }
`;

const Headline = styled.h1`
    ${FONTS.SEMIBOLD}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(32, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(38.4, 'm')};
    margin: 0 0 ${clampCss(56, 'm')} 0;
    text-align: center;
    text-transform: uppercase;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(32, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(38.4, 't')};
        margin: 0 0 ${clampCss(56, 'm')} 0;
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(46px, ${pxToVw(46, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(55.2px, ${pxToVw(55.2, 'd')});
        margin: 0 0 max(56px, ${pxToVw(56, 'd')}) 0;
    }
`;

const Group = styled.div`
    margin-bottom: ${clampCss(40, 'm')};

    ${MEDIA.TABLET.UP} {
        margin-bottom: ${clampCss(40, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        margin-bottom: max(40px, ${pxToVw(40, 'd')});
    }
`;

const GroupHead = styled.h3`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(20, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(24, 'm')};
    margin: 0 0 ${clampCss(16, 'm')} 0;
    text-align: left;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(20, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(24, 't')};
        margin: 0 0 ${clampCss(16, 't')} 0;
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(20px, ${pxToVw(20, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(24px, ${pxToVw(24, 'd')});
        margin: 0 0 max(16px, ${pxToVw(16, 'd')}) 0;
    }
`;

/**
 * Gets static props needed for the actual page.
 *
 * @export
 * @param {Object} ctx    The context in which this function is called.
 * @param {Object} params The params given for this page.
 * @returns {Object} An props object or an redirect.
 */
export async function getStaticProps() {
    let menuJson = {};
    let footerJson = {};
    let pageJson = {};

    try {
        await dbConnect();
        const menu = await PageConfig.findOne({type: 'Menu'});
        const footer = await PageConfig.findOne({type: 'Footer'})
            .populate('partner')
            .populate('socialMedia.icon');
        const page = await PageConfig.findOne({type: 'FAQ'})
            .select('-createdAt -updatedAt');

        await populateMenuItems(menu.mainMenu);
        await populateFooterItems(footer.copyright);

        menuJson = JSON.stringify(menu.toObject());
        footerJson = JSON.stringify(footer.toObject());
        pageJson = JSON.stringify(page.toObject());
    } catch (e) {
        menuJson = JSON.stringify({});
        footerJson = JSON.stringify({});
        pageJson = JSON.stringify({
            faq: [],
            seoKeywordList: []
        });
    }

    const menuProp = JSON.parse(menuJson);

    menuProp.active = '/faq';

    return {
        props: {
            footer: JSON.parse(footerJson),
            menu: menuProp,
            page: JSON.parse(pageJson)
        },
        revalidate: 2
    };
}